import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useShippingCountries } from '@/hooks/useShippingCountries';
import { ShippingCountryType } from '@/types/order';
import { Image } from '@/components/Image';

interface AvailableCountriesModalProps {
  onClose: () => void;
}

// 국가명 첫 글자를 그룹 키로 사용. 이름이 비어 있으면 '#' 그룹으로 분류
const UNKNOWN_GROUP_KEY = '#';

const getGroupKey = (country: ShippingCountryType) =>
  country.name[0]?.toUpperCase() ?? UNKNOWN_GROUP_KEY;

// 국가 배열을 알파벳 첫 글자별로 묶은 객체로 변환
const groupCountriesByFirstLetter = (countries: ShippingCountryType[]) => {
  const sortedByName = [...countries].sort((a, b) => a.name.localeCompare(b.name));

  return sortedByName.reduce<Record<string, ShippingCountryType[]>>((groups, country) => {
    const key = getGroupKey(country);
    groups[key] ??= [];
    groups[key].push(country);
    return groups;
  }, {});
};

const AvailableCountriesModal = ({ onClose }: AvailableCountriesModalProps) => {
  const { countries } = useShippingCountries();

  const groupedCountries = useMemo(() => groupCountriesByFirstLetter(countries), [countries]);

  return (
    <div
      className="fixed inset-0 w-full h-full bg-black/80 flex justify-center items-center z-[1100]"
      onClick={onClose}
    >
      <div
        className="rounded-2xl bg-white p-10 w-[680px] max-h-[80vh] flex flex-col gap-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center cursor-pointer" onClick={onClose}>
          <Image src="/icons/close.svg" alt="close-svg" width={24} height={24} />
        </button>

        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-[30px] leading-9 font-extrabold">
            <FormattedMessage id="available_countries" defaultMessage="Available countries" />
          </h1>
          <h3 className="text-[20px] leading-6 text-[#4D4D4D] font-medium">
            <FormattedMessage
              id="international_shipping_available_description"
              defaultMessage="International shipping is available to the following countries."
            />
          </h3>
        </div>

        <div className="overflow-y-auto no-scrollbar flex flex-col gap-5 pr-1">
          {Object.entries(groupedCountries).map(([letter, countries]) => (
            <div key={letter} className="flex flex-col gap-3">
              <p className="text-[12px] font-semibold text-[#262626]">{letter}</p>
              <div className="flex flex-wrap gap-4">
                {countries.map((country) => (
                  <span
                    key={country.id}
                    className="px-4 py-3 rounded-full bg-[#F5F5F5] text-[16px] text-[#262626]"
                  >
                    {country.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AvailableCountriesModal;
